/**
 * BADGE_CATALOG · 84 базовых бейджа (Block 1-4) + 9 экзамен-вех (checkpoint_exams ·
 * Genie 25 JUN: 3 «взят» tier 1 · 3 «налегке» tier 2 · «все рубежи» tier 3 ·
 * «первая попытка»/«работа над ошибками» tier 4) = 93 badges (esbuild-loaded count;
 * 6 owl-of-wisdom-sprint генерятся .map-ом, поэтому литеральных id-строк меньше).
 *
 * Block 1: per `14-achievements-spec.md` §2 (post-Genie-ack 2026-05-01) · 32 entries.
 * Block 2: per `14-achievements-spec.md` §3.1 (W3.1 backfill + W4.1 Tier 4 curiosity).
 * Block 3: per `14-achievements-spec.md` §3.2 + Q2=A (W3.1 + W4.1 Tier 4 curiosity).
 * Block 4: per `14-achievements-spec.md` §3.3 + Q3=A spec-as-is + W4.1 Tier 4 curiosity.
 *
 * Tier 1: Sprint completion · 22 (B1 6 + v0.5 + B2 7 + v1.0 + B3 4 + v1.5 + B4 2 + v2.0).
 * Tier 2: Sprint perfectionist · 14 (B1 S04-S06 + B2 S07-S13 + B3 S14-S17).
 * Tier 3: Skill rank progression · 25 (Detective ×3 + Author ×3 + CTFL ×3 + API Master ×3 + SQL Detective ×3 + Security Watcher ×2 + Automation Architect ×3 + CI Wrangler ×2 + AI QA Master ×3).
 * Tier 4: Curiosity rewards · 25 (B1 13 + B2 4 + B3 3 + B4 4 + cross-block 1 since polyglot already block-1's «cross-block» membership preserved).
 *
 * W4.1 (2026-05-04 · Genie ack Q3=A + Q6=A): Block 4 catalog (3 cores+release + 3 ranks +
 * 4 Tier 4 curiosity) + 11 deferred Tier 4 curiosity badges (B2 4 + B3 3 + B4 4) using new
 * `capability-flag` BadgeTrigger kind · CF-W3.1-03 unblocked.
 *
 * Moved from `app/src/lib/achievements/badge-catalog.ts` per ADR-008 · Phase 5
 * server-side re-evaluation imports from `@shared/engine/*` without rewrite.
 */

import type { BadgeDefinition } from '../schemas';

export const BADGE_CATALOG: ReadonlyArray<BadgeDefinition> = [
  // ─── Tier 1 · Sprint cores + Release v0.5 ────────────────────────────
  {
    id: 'sprint-01-core',
    icon: 'core-mindset',
    name: 'Sprint 01 Mindset Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-1',
    description: 'Все Required тикеты Sprint 01 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 1 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['QA Concepts'],
  },
  {
    id: 'sprint-02-core',
    icon: 'core-requirements',
    name: 'Sprint 02 Requirements Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-1',
    description: 'Все Required тикеты Sprint 02 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 2 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Requirements Analysis'],
  },
  {
    id: 'sprint-03-core',
    icon: 'core-test-types',
    name: 'Sprint 03 Test Types Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-1',
    description: 'Все Required тикеты Sprint 03 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 3 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Test Types'],
  },
  {
    id: 'sprint-04-core',
    icon: 'core-docs',
    name: 'Sprint 04 Test Documentation Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-1',
    description: 'Все Required тикеты Sprint 04 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 4 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Test Documentation'],
  },
  {
    id: 'sprint-05-core',
    icon: 'core-test-design',
    name: 'Sprint 05 Test Design Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-1',
    description: 'Все Required тикеты Sprint 05 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 5 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Test Design'],
  },
  {
    id: 'sprint-06-ctfl-ready',
    icon: 'ctfl-cap',
    name: 'Sprint 06 CTFL-Ready',
    emoji: '🎓',
    tier: 1,
    block: 'block-1',
    description: 'Все Required Sprint 06 + Mock Exam #2 ≥ 65%',
    trigger: { kind: 'sprint-required-complete', sprintNum: 6 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'istqb-path-widget'],
    skillsCrossRef: ['ISTQB Knowledge'],
  },
  {
    id: 'release-v0-5-trainee',
    icon: 'rank-trainee',
    name: 'Trainee QA · CTFL-Ready',
    emoji: '🎓',
    tier: 1,
    block: 'block-1',
    description: 'Block 1 Capstone сдан + 6 sprint cores + CTFL Mock #2 ≥ 65%',
    trigger: { kind: 'release-unlock', releaseVersion: 'v0.5' },
    uiPlacement: ['profile-wall-hero', 'capstone-ceremony', 'toast-notification'],
    skillsCrossRef: ['QA Concepts', 'Test Documentation', 'Test Design', 'ISTQB Knowledge'],
  },

  // ─── Tier 2 · Atelier Resident (S04-S06 · Block 1 Practice debut) ────
  {
    id: 'atelier-resident-s04',
    icon: 'atelier-resident',
    name: 'Atelier Resident · Sprint 04',
    emoji: '🌟',
    tier: 2,
    block: 'block-1',
    description: 'Все Required + Bonus Sprint 04 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 4 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Practice Lab'],
  },
  {
    id: 'atelier-resident-s05',
    icon: 'atelier-resident',
    name: 'Atelier Resident · Sprint 05',
    emoji: '🌟',
    tier: 2,
    block: 'block-1',
    description: 'Все Required + Bonus Sprint 05 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 5 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Practice Lab'],
  },
  {
    id: 'atelier-resident-s06',
    icon: 'atelier-resident',
    name: 'Atelier Resident · Sprint 06',
    emoji: '🌟',
    tier: 2,
    block: 'block-1',
    description: 'Все Required + Bonus Sprint 06 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 6 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['ISTQB Knowledge', 'Practice Lab'],
  },

  // ─── Tier 3 · Detective ranks ────────────────────────────────────────
  {
    id: 'detective-junior',
    icon: 'rank-detective',
    name: 'Detective Junior',
    emoji: '🔍',
    tier: 3,
    block: 'block-1',
    description: '5+ Investigate-сценариев решены',
    trigger: { kind: 'rank-threshold', rankType: 'detective', threshold: 5 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
  },
  {
    id: 'detective-senior',
    icon: 'rank-detective',
    name: 'Detective Senior',
    emoji: '🕵️',
    tier: 3,
    block: 'block-1',
    description: '15+ Investigate-сценариев решены',
    trigger: { kind: 'rank-threshold', rankType: 'detective', threshold: 15 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
  },
  {
    id: 'detective-master',
    icon: 'rank-detective',
    name: 'Detective Master',
    emoji: '🧠',
    tier: 3,
    block: 'block-1',
    description: '30+ Investigate-сценариев решены',
    trigger: { kind: 'rank-threshold', rankType: 'detective', threshold: 30 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
  },

  // ─── Tier 3 · Author ranks ───────────────────────────────────────────
  {
    id: 'author-apprentice',
    icon: 'rank-author',
    name: 'Author Apprentice',
    emoji: '✍️',
    tier: 3,
    block: 'block-1',
    description: '5+ Author-сценариев пройдены (rubric ≥ pass threshold)',
    trigger: { kind: 'rank-threshold', rankType: 'author', threshold: 5 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Test Documentation'],
  },
  {
    id: 'author-senior',
    icon: 'rank-author',
    name: 'Author Senior',
    emoji: '📝',
    tier: 3,
    block: 'block-1',
    description: '15+ Author-сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'author', threshold: 15 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Test Documentation'],
  },
  {
    id: 'author-master',
    icon: 'rank-author',
    name: 'Author Master',
    emoji: '📜',
    tier: 3,
    block: 'block-1',
    description: '30+ Author-сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'author', threshold: 30 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Test Documentation'],
  },

  // ─── Tier 3 · CTFL ranks (Mock Exam #2) ──────────────────────────────
  {
    id: 'ctfl-pass',
    icon: 'rank-ctfl',
    name: 'CTFL Pass',
    emoji: '🎓',
    tier: 3,
    block: 'block-1',
    description: 'Mock Exam #2 ≥ 65% (real-exam pass threshold)',
    trigger: { kind: 'rank-threshold', rankType: 'ctfl', threshold: 65 },
    uiPlacement: ['profile-wall-section', 'istqb-path-widget'],
    skillsCrossRef: ['ISTQB Knowledge'],
  },
  {
    id: 'ctfl-honors',
    icon: 'rank-ctfl',
    name: 'CTFL Honors',
    emoji: '🥈',
    tier: 3,
    block: 'block-1',
    description: 'Mock Exam #2 ≥ 80%',
    trigger: { kind: 'rank-threshold', rankType: 'ctfl', threshold: 80 },
    uiPlacement: ['profile-wall-section', 'istqb-path-widget'],
    skillsCrossRef: ['ISTQB Knowledge'],
  },
  {
    id: 'ctfl-distinction',
    icon: 'rank-ctfl',
    name: 'CTFL Distinction',
    emoji: '🥇',
    tier: 3,
    block: 'block-1',
    description: 'Mock Exam #2 ≥ 90%',
    trigger: { kind: 'rank-threshold', rankType: 'ctfl', threshold: 90 },
    uiPlacement: ['profile-wall-section', 'istqb-path-widget'],
    skillsCrossRef: ['ISTQB Knowledge'],
  },

  // ─── Tier 4 · Owl of Wisdom (per-sprint × 6 + Block 1) ──────────────
  ...[1, 2, 3, 4, 5, 6].map<BadgeDefinition>((n) => ({
    id: `owl-of-wisdom-sprint-${n.toString().padStart(2, '0')}`,
    name: `Owl of Wisdom · Sprint ${n.toString().padStart(2, '0')}`,
    emoji: '🦉',
    icon: 'owl',
    tier: 4,
    block: 'block-1',
    description: `Все «<details class="senior-extension">» в Sprint ${n} открыты`,
    trigger: { kind: 'all-senior-extensions-opened', sprintNum: n },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  })),
  {
    id: 'owl-of-wisdom-block-1',
    icon: 'owl',
    name: 'Owl of Wisdom · Block 1',
    emoji: '🦉',
    tier: 4,
    block: 'block-1',
    description: 'Owl earned для всех 6 sprint Block 1',
    trigger: { kind: 'all-senior-extensions-opened', block: 'block-1' },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-hero'],
  },

  // ─── Tier 4 · Curiosity grab-bag ─────────────────────────────────────
  {
    id: 'curious-cat',
    icon: 'curious-cat',
    name: 'Curious Cat',
    emoji: '🧠',
    tier: 4,
    block: 'cross-block',
    description: '10+ Bonus-сценариев пройдены',
    trigger: { kind: 'cumulative-bonus-count', threshold: 10 },
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'atelier-champion-block-1',
    icon: 'trophy',
    name: 'Atelier Champion · Block 1',
    emoji: '🏆',
    tier: 4,
    block: 'block-1',
    description: 'Все Bonus артефакты Capstone Block 1 сданы (rubric 9/9)',
    trigger: { kind: 'capstone-bonus-complete', capstoneId: 'block-1' },
    uiPlacement: ['profile-wall-hero', 'capstone-ceremony'],
  },
  {
    id: 'polyglot',
    icon: 'polyglot',
    name: 'Polyglot',
    emoji: '📚',
    tier: 4,
    block: 'cross-block',
    description: 'Все 3 темы переключены + RU/EN-локали тоже',
    trigger: { kind: 'theme-locale-polyglot' },
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'glossary-master-block-1',
    icon: 'glossary',
    name: 'Glossary Master · Block 1',
    emoji: '📚',
    tier: 4,
    block: 'block-1',
    description: 'Все 155 ISTQB-терминов мастерены через SM-2 флешкарты',
    trigger: { kind: 'all-glossary-mastered', threshold: 155 },
    uiPlacement: ['profile-wall-section', 'istqb-path-widget'],
    skillsCrossRef: ['ISTQB Knowledge'],
  },
  {
    id: 'early-bird',
    icon: 'early-bird',
    name: 'Early Bird',
    emoji: '⏰',
    tier: 4,
    block: 'cross-block',
    description: '5 Pomodoro-сессий до 8 утра',
    trigger: { kind: 'pomodoro-time-window', window: 'early-bird', threshold: 5 },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'first-quick-check',
    icon: 'first-check',
    name: 'First Quick Check',
    emoji: '🎉',
    tier: 4,
    block: 'block-1',
    description: 'Первый правильный Quick Check ответ',
    trigger: { kind: 'first-quick-check-correct' },
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },

  // ════════════════════════════════════════════════════════════════════════
  // BLOCK 2 · S07-S12 · «Junior Manual+ QA» · Release v1.0
  // Per `14-achievements-spec.md` §3.1 · W3.1 backfill 2026-05-04
  // ════════════════════════════════════════════════════════════════════════

  // ─── Tier 1 · Sprint cores (S07-S12) + Release v1.0 ─────────────────
  {
    id: 'sprint-07-core',
    name: 'Sprint 07 Workspace Tools Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-2',
    description: 'Все Required тикеты Sprint 07 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 7 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Workspace Tools'],
  },
  {
    id: 'sprint-08-core',
    name: 'Sprint 08 SQL Basic Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-2',
    description: 'Все Required тикеты Sprint 08 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 8 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['SQL'],
  },
  {
    id: 'sprint-09-core',
    name: 'Sprint 09 HTTP/REST API Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-2',
    description: 'Все Required тикеты Sprint 09 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 9 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['API'],
  },
  {
    id: 'sprint-10-core',
    name: 'Sprint 10 Postman Master Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-2',
    description: 'Все Required тикеты Sprint 10 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 10 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['API'],
  },
  {
    id: 'sprint-11-core',
    name: 'Sprint 11 SQL Deep Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-2',
    description: 'Все Required тикеты Sprint 11 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 11 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['SQL'],
  },
  {
    id: 'sprint-12-core',
    name: 'Sprint 12 Perf + Security Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-2',
    description: 'Все Required тикеты Sprint 12 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 12 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Security', 'Non-functional Testing'],
  },
  {
    id: 'sprint-13-core',
    name: 'Sprint 13 Mobile Testing Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-2',
    description: 'Все Required тикеты Sprint 13 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 13 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Mobile Testing'],
  },
  {
    id: 'release-v1-0-junior-manual-plus',
    icon: 'release',
    name: 'Junior Manual+ QA · Release v1.0',
    emoji: '🎓',
    tier: 1,
    block: 'block-2',
    description: 'Block 2 Capstone сдан + 7 sprint cores S07-S13',
    trigger: { kind: 'release-unlock', releaseVersion: 'v1.0' },
    uiPlacement: ['profile-wall-hero', 'capstone-ceremony', 'toast-notification'],
    skillsCrossRef: ['API', 'SQL', 'Workspace Tools', 'Security'],
  },

  // ─── Tier 2 · Atelier Resident (S07-S12) ───────────────────────────
  {
    id: 'atelier-resident-s07',
    name: 'Atelier Resident · Sprint 07',
    emoji: '🌟',
    tier: 2,
    block: 'block-2',
    description: 'Все Required + Bonus Sprint 07 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 7 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Workspace Tools', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s08',
    name: 'Atelier Resident · Sprint 08',
    emoji: '🌟',
    tier: 2,
    block: 'block-2',
    description: 'Все Required + Bonus Sprint 08 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 8 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['SQL', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s09',
    name: 'Atelier Resident · Sprint 09',
    emoji: '🌟',
    tier: 2,
    block: 'block-2',
    description: 'Все Required + Bonus Sprint 09 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 9 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['API', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s10',
    name: 'Atelier Resident · Sprint 10',
    emoji: '🌟',
    tier: 2,
    block: 'block-2',
    description: 'Все Required + Bonus Sprint 10 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 10 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['API', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s11',
    name: 'Atelier Resident · Sprint 11',
    emoji: '🌟',
    tier: 2,
    block: 'block-2',
    description: 'Все Required + Bonus Sprint 11 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 11 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['SQL', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s12',
    name: 'Atelier Resident · Sprint 12',
    emoji: '🌟',
    tier: 2,
    block: 'block-2',
    description: 'Все Required + Bonus Sprint 12 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 12 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Security', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s13',
    name: 'Atelier Resident · Sprint 13',
    emoji: '🌟',
    tier: 2,
    block: 'block-2',
    description: 'Все Required + Bonus Sprint 13 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 13 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Mobile Testing', 'Practice Lab'],
  },

  // ─── Tier 3 · API Master ranks ─────────────────────────────────────
  {
    id: 'api-master-junior',
    icon: 'api-master',
    name: 'API Master · Junior',
    emoji: '🔌',
    tier: 3,
    block: 'block-2',
    description: '5+ API сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'api-master', threshold: 5 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['API'],
  },
  {
    id: 'api-master-senior',
    icon: 'api-master',
    name: 'API Master · Senior',
    emoji: '🔌',
    tier: 3,
    block: 'block-2',
    description: '15+ API сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'api-master', threshold: 15 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['API'],
  },
  {
    id: 'api-master-master',
    icon: 'api-master',
    name: 'API Master · Master',
    emoji: '🔌',
    tier: 3,
    block: 'block-2',
    description: '30+ API сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'api-master', threshold: 30 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['API'],
  },

  // ─── Tier 3 · SQL Detective ranks ──────────────────────────────────
  {
    id: 'sql-detective-junior',
    icon: 'sql-detective',
    name: 'SQL Detective · Junior',
    emoji: '🗄',
    tier: 3,
    block: 'block-2',
    description: '5+ SQL сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'sql-detective', threshold: 5 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['SQL'],
  },
  {
    id: 'sql-detective-senior',
    icon: 'sql-detective',
    name: 'SQL Detective · Senior',
    emoji: '🗄',
    tier: 3,
    block: 'block-2',
    description: '15+ SQL сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'sql-detective', threshold: 15 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['SQL'],
  },
  {
    id: 'sql-detective-master',
    icon: 'sql-detective',
    name: 'SQL Detective · Master',
    emoji: '🗄',
    tier: 3,
    block: 'block-2',
    description: '30+ SQL сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'sql-detective', threshold: 30 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['SQL'],
  },

  // ─── Tier 3 · Security Watcher ranks (2-tier per spec) ─────────────
  {
    id: 'security-watcher-junior',
    icon: 'security-watcher',
    name: 'Security Watcher · Junior',
    emoji: '🛡',
    tier: 3,
    block: 'block-2',
    description: '3+ security сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'security-watcher', threshold: 3 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Security'],
  },
  {
    id: 'security-watcher-senior',
    icon: 'security-watcher',
    name: 'Security Watcher · Senior',
    emoji: '🛡',
    tier: 3,
    block: 'block-2',
    description: '10+ security сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'security-watcher', threshold: 10 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Security'],
  },

  // ════════════════════════════════════════════════════════════════════════
  // BLOCK 3 · S14-S17 · «Junior Auto QA» · Release v1.5
  // Per `14-achievements-spec.md` §3.2 + Genie ack 2026-05-04 Q2=A (2 ranks)
  // Renumber 2026-06-28: S13 Mobile → Block 2; auto cores S13-16 → S14-17 (sprints.ts канон).
  // ════════════════════════════════════════════════════════════════════════

  // ─── Tier 1 · Sprint cores (S14-S17) + Release v1.5 ─────────────────
  {
    id: 'sprint-14-core',
    name: 'Sprint 14 Automation Basics Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-3',
    description: 'Все Required тикеты Sprint 14 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 14 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Automation Basics'],
  },
  {
    id: 'sprint-15-core',
    name: 'Sprint 15 pytest + POM Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-3',
    description: 'Все Required тикеты Sprint 15 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 15 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Automation Patterns'],
  },
  {
    id: 'sprint-16-core',
    name: 'Sprint 16 Selenium Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-3',
    description: 'Все Required тикеты Sprint 16 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 16 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['Automation Modern'],
  },
  {
    id: 'sprint-17-core',
    name: 'Sprint 17 CI/CD Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-3',
    description: 'Все Required тикеты Sprint 17 закрыты',
    trigger: { kind: 'sprint-required-complete', sprintNum: 17 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['CI/CD'],
  },
  {
    id: 'release-v1-5-junior-auto',
    icon: 'release',
    name: 'Junior Auto QA · Release v1.5',
    emoji: '🎓',
    tier: 1,
    block: 'block-3',
    description: 'Block 3 Capstone сдан + 4 sprint cores S14-S17',
    trigger: { kind: 'release-unlock', releaseVersion: 'v1.5' },
    uiPlacement: ['profile-wall-hero', 'capstone-ceremony', 'toast-notification'],
    skillsCrossRef: ['Automation Basics', 'Automation Patterns', 'Automation Modern', 'CI/CD'],
  },

  // ─── Tier 2 · Atelier Resident (S13-S16) ───────────────────────────
  {
    id: 'atelier-resident-s14',
    name: 'Atelier Resident · Sprint 14',
    emoji: '🌟',
    tier: 2,
    block: 'block-3',
    description: 'Все Required + Bonus Sprint 14 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 14 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Automation Basics', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s15',
    name: 'Atelier Resident · Sprint 15',
    emoji: '🌟',
    tier: 2,
    block: 'block-3',
    description: 'Все Required + Bonus Sprint 15 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 15 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Automation Patterns', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s16',
    name: 'Atelier Resident · Sprint 16',
    emoji: '🌟',
    tier: 2,
    block: 'block-3',
    description: 'Все Required + Bonus Sprint 16 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 16 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['Automation Modern', 'Practice Lab'],
  },
  {
    id: 'atelier-resident-s17',
    name: 'Atelier Resident · Sprint 17',
    emoji: '🌟',
    tier: 2,
    block: 'block-3',
    description: 'Все Required + Bonus Sprint 17 закрыты',
    trigger: { kind: 'sprint-bonus-complete', sprintNum: 17 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section'],
    skillsCrossRef: ['CI/CD', 'Practice Lab'],
  },

  // ─── Tier 3 · Automation Architect ranks ──────────────────────────
  {
    id: 'automation-architect-junior',
    name: 'Automation Architect · Junior',
    emoji: '🤖',
    tier: 3,
    block: 'block-3',
    description: '5+ automation сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'automation-architect', threshold: 5 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Automation Basics', 'Automation Patterns'],
  },
  {
    id: 'automation-architect-senior',
    name: 'Automation Architect · Senior',
    emoji: '🤖',
    tier: 3,
    block: 'block-3',
    description: '15+ automation сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'automation-architect', threshold: 15 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Automation Basics', 'Automation Patterns', 'Automation Modern'],
  },
  {
    id: 'automation-architect-master',
    name: 'Automation Architect · Master',
    emoji: '🤖',
    tier: 3,
    block: 'block-3',
    description: '30+ automation сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'automation-architect', threshold: 30 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['Automation Basics', 'Automation Patterns', 'Automation Modern'],
  },

  // ─── Tier 3 · CI Wrangler ranks (2-tier per spec) ─────────────────
  {
    id: 'ci-wrangler-junior',
    icon: 'ci-wrangler',
    name: 'CI Wrangler · Junior',
    emoji: '⚙️',
    tier: 3,
    block: 'block-3',
    description: '3+ CI/pipeline сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'ci-wrangler', threshold: 3 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['CI/CD'],
  },
  {
    id: 'ci-wrangler-senior',
    icon: 'ci-wrangler',
    name: 'CI Wrangler · Senior',
    emoji: '⚙️',
    tier: 3,
    block: 'block-3',
    description: '10+ CI/pipeline сценариев пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'ci-wrangler', threshold: 10 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['CI/CD'],
  },

  // ════════════════════════════════════════════════════════════════════════
  // BLOCK 4 · S18-S19 · «QA Engineer · Graduated» · Release v2.0
  // Per `14-achievements-spec.md` §3.3 + Genie ack 2026-05-04 Q3=A (spec as-is)
  // Renumber 2026-06-28: AI/Career cores S17-18 → S18-19 (sprints.ts канон).
  // ════════════════════════════════════════════════════════════════════════

  // ─── Tier 1 · Sprint cores (S18-S19) + Release v2.0 ─────────────────
  {
    id: 'sprint-18-core',
    name: 'Sprint 18 AI Mastery Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-4',
    description: 'Все Required тикеты Sprint 18 закрыты + Capstone S18 ship',
    trigger: { kind: 'sprint-required-complete', sprintNum: 18 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['AI Workflow'],
  },
  {
    id: 'sprint-19-core',
    name: 'Sprint 19 Career Launch Core',
    emoji: '🎯',
    tier: 1,
    block: 'block-4',
    description: 'Все Required тикеты Sprint 19 закрыты · portfolio готов',
    trigger: { kind: 'sprint-required-complete', sprintNum: 19 },
    uiPlacement: ['sprint-card-shimmer', 'profile-wall-section', 'sprint-review-ceremony'],
    skillsCrossRef: ['QA Concepts'],
  },
  {
    id: 'release-v2-0-graduated',
    icon: 'release',
    name: 'QA Engineer · Graduated · Release v2.0',
    emoji: '🎓',
    tier: 1,
    block: 'block-4',
    description: 'Block 4 Capstone сдан + 2 sprint cores S18-S19 + portfolio exported',
    trigger: { kind: 'release-unlock', releaseVersion: 'v2.0' },
    uiPlacement: ['profile-wall-hero', 'capstone-ceremony', 'toast-notification'],
    skillsCrossRef: ['AI Workflow', 'QA Concepts'],
  },

  // ─── Tier 3 · AI QA Master ranks ──────────────────────────────────
  {
    id: 'ai-qa-master-junior',
    icon: 'ai-qa-master',
    name: 'AI QA Master · Junior',
    emoji: '🧠',
    tier: 3,
    block: 'block-4',
    description: '5+ AI scenarios пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'ai-qa-master', threshold: 5 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['AI Workflow'],
  },
  {
    id: 'ai-qa-master-senior',
    icon: 'ai-qa-master',
    name: 'AI QA Master · Senior',
    emoji: '🧠',
    tier: 3,
    block: 'block-4',
    description: '15+ AI scenarios пройдены',
    trigger: { kind: 'rank-threshold', rankType: 'ai-qa-master', threshold: 15 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['AI Workflow'],
  },
  {
    id: 'ai-qa-master-master',
    icon: 'ai-qa-master',
    name: 'AI QA Master · Master',
    emoji: '🧠',
    tier: 3,
    block: 'block-4',
    description: '30+ AI scenarios пройдены (rare · spans CT-AI Extension)',
    trigger: { kind: 'rank-threshold', rankType: 'ai-qa-master', threshold: 30 },
    uiPlacement: ['topbar-rank-chip', 'profile-wall-section'],
    skillsCrossRef: ['AI Workflow'],
  },

  // ════════════════════════════════════════════════════════════════════════
  // TIER 4 CURIOSITY · Block 2-4 deferred badges (W4.1 · CF-W3.1-03 unblock)
  // Per Genie ack 2026-05-04 Q6=A · ship all 11 в одном pass · capability-flag
  // BadgeTrigger kind formalized.
  // ════════════════════════════════════════════════════════════════════════

  // ─── Block 2 · 4 curiosity badges (W4.1 backfill from CF-W2-final-07 deferred) ─
  {
    id: 'bug-hunter-block-2',
    icon: 'bug-hunter',
    name: 'Bug Hunter · Block 2',
    emoji: '🐛',
    tier: 4,
    block: 'block-2',
    description: '5+ непреднамеренных security flaws найдено в сценариях',
    trigger: { kind: 'capability-flag', flag: 'b2-security-flaw-found', threshold: 5 },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'first-api-request',
    icon: 'first-api-request',
    name: 'First API Request',
    emoji: '🚀',
    tier: 4,
    block: 'block-2',
    description: 'Первый успешный Postman-equivalent request',
    trigger: { kind: 'capability-flag', flag: 'first-api-request' },
    hiddenUntilEarned: true,
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'hacker-mindset',
    icon: 'hacker-mindset',
    name: 'Hacker Mindset',
    emoji: '💀',
    tier: 4,
    block: 'block-2',
    description: 'Все OWASP Top 10 сценарии решены',
    trigger: { kind: 'capability-flag', flag: 'owasp-top-10-complete' },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'polyglot-block-2',
    icon: 'polyglot-block-2',
    name: 'Polyglot · Block 2',
    emoji: '🦜',
    tier: 4,
    block: 'block-2',
    description: 'Все 3 темы + RU/EN + TR-локаль (post-Phase 5 ship)',
    trigger: { kind: 'capability-flag', flag: 'polyglot-tr' },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },

  // ─── Block 3 · 3 curiosity badges (W4.1 backfill from W3.1 deferred) ────
  {
    id: 'selenium-recorder',
    icon: 'selenium-recorder',
    name: 'Selenium Recorder',
    emoji: '🎬',
    tier: 4,
    block: 'block-3',
    description: 'Записал первую session через CodeGen-style recorder',
    trigger: { kind: 'capability-flag', flag: 'selenium-codegen-used' },
    hiddenUntilEarned: true,
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'flake-slayer',
    icon: 'flake-slayer',
    name: 'Flake Slayer',
    emoji: '🪲',
    tier: 4,
    block: 'block-3',
    description: 'Починил flaky тест с 30%+ failure rate',
    trigger: { kind: 'capability-flag', flag: 'flake-slayer-fixed' },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'coverage-champion',
    icon: 'coverage-champion',
    name: 'Coverage Champion',
    emoji: '📊',
    tier: 4,
    block: 'block-3',
    description: 'Automation suite покрывает 80%+ Block 1 e2e flows',
    trigger: { kind: 'capability-flag', flag: 'coverage-80-plus' },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },

  // ─── Block 4 · 4 curiosity badges (W4.1 ship · per spec §3.3) ──────────
  {
    id: 'prompt-injector',
    icon: 'prompt-injector',
    name: 'Prompt Injector',
    emoji: '💉',
    tier: 4,
    block: 'block-4',
    description: 'Нашёл prompt injection в 3+ AI сценариях',
    trigger: { kind: 'capability-flag', flag: 'prompt-injection-found', threshold: 3 },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'bias-detector',
    icon: 'bias-detector',
    name: 'Bias Detector',
    emoji: '🎭',
    tier: 4,
    block: 'block-4',
    description: 'Идентифицировал bias в 3+ recommendation/sentiment сценариях',
    trigger: { kind: 'capability-flag', flag: 'bias-detected', threshold: 3 },
    hiddenUntilEarned: true,
    uiPlacement: ['profile-wall-section'],
  },
  {
    id: 'shipped-v2-graduated',
    icon: 'shipped-v2',
    name: 'Shipped · v2.0 Graduated',
    emoji: '🚀',
    tier: 4,
    block: 'block-4',
    description: 'Sprint 17 capstone shipped end-to-end (idea → tests → release)',
    trigger: { kind: 'capability-flag', flag: 'sprint-17-shipped' },
    uiPlacement: ['profile-wall-hero', 'capstone-ceremony', 'toast-notification'],
  },
  {
    id: 'career-launch-s18',
    icon: 'career-launch',
    name: 'Career Launch · Sprint 18',
    emoji: '🎖',
    tier: 4,
    block: 'block-4',
    description: 'Sprint 18 portfolio exported · LinkedIn checklist · mock interview complete',
    trigger: { kind: 'capability-flag', flag: 'career-launch-complete' },
    uiPlacement: ['profile-wall-hero', 'capstone-ceremony', 'toast-notification'],
  },

  // ─── Экзамены-вехи (checkpoint_exams · Genie 25 JUN) ──────────────────────
  // Награды за контрольные экзамены (S05/S09/S13). Через capability-flag, флаги
  // выставляет ProgressContext.recordExamAchievementFlags на сдаче BlockExam.
  {
    id: 'exam-block1-passed',
    name: 'Перевал взят',
    emoji: '⛰️',
    icon: 'exam-peak-flag',
    tier: 1,
    block: 'block-1',
    description: 'Сдан экзамен-веха «Перевал» по Спринтам 1-5',
    trigger: { kind: 'capability-flag', flag: 'exam-block1-passed' },
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-block2-passed',
    name: 'Веха пройдена',
    emoji: '🚩',
    icon: 'exam-signpost',
    tier: 1,
    block: 'block-2',
    description: 'Сдан контрольный экзамен по Спринтам 1-9',
    trigger: { kind: 'capability-flag', flag: 'exam-block2-passed' },
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-block3-passed',
    name: 'Большой рубеж',
    emoji: '🏔️',
    icon: 'exam-summit',
    tier: 1,
    block: 'block-3',
    description: 'Сдан большой накопительный экзамен по Спринтам 1-13',
    trigger: { kind: 'capability-flag', flag: 'exam-block3-passed' },
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-block1-aced',
    name: 'Перевал налегке',
    emoji: '✨',
    icon: 'exam-ace-star',
    tier: 2,
    block: 'block-1',
    description: 'Экзамен «Перевал» сдан на 90% и выше',
    trigger: { kind: 'capability-flag', flag: 'exam-block1-aced' },
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-block2-aced',
    name: 'Веха налегке',
    emoji: '✨',
    icon: 'exam-ace-star',
    tier: 2,
    block: 'block-2',
    description: 'Контрольный экзамен S1-9 сдан на 90% и выше',
    trigger: { kind: 'capability-flag', flag: 'exam-block2-aced' },
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-block3-aced',
    name: 'Рубеж налегке',
    emoji: '✨',
    icon: 'exam-ace-star',
    tier: 2,
    block: 'block-3',
    description: 'Большой экзамен S1-13 сдан на 90% и выше',
    trigger: { kind: 'capability-flag', flag: 'exam-block3-aced' },
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-all-checkpoints',
    name: 'Все рубежи взяты',
    emoji: '🎖️',
    icon: 'exam-laurel',
    tier: 3,
    block: 'cross-block',
    description: 'Сданы все контрольные экзамены-вехи курса',
    trigger: { kind: 'capability-flag', flag: 'exam-passed-count', threshold: 3 },
    uiPlacement: ['profile-wall-hero', 'toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-first-try',
    name: 'С первой попытки',
    emoji: '🎯',
    icon: 'exam-bullseye',
    tier: 4,
    block: 'cross-block',
    description: 'Контрольный экзамен сдан с первой попытки, без пересдачи',
    trigger: { kind: 'capability-flag', flag: 'exam-first-try' },
    hiddenUntilEarned: true,
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
  {
    id: 'exam-comeback',
    name: 'Работа над ошибками',
    emoji: '💪',
    icon: 'exam-comeback',
    tier: 4,
    block: 'cross-block',
    description: 'Завалил экзамен, вернулся и сдал на пересдаче',
    trigger: { kind: 'capability-flag', flag: 'exam-comeback' },
    hiddenUntilEarned: true,
    uiPlacement: ['toast-notification', 'profile-wall-section'],
  },
];

export const BADGES_BY_ID: Record<string, BadgeDefinition> = Object.fromEntries(
  BADGE_CATALOG.map((b) => [b.id, b]),
);

export function badgeById(id: string): BadgeDefinition | undefined {
  return BADGES_BY_ID[id];
}

// Каталог: 93 бейджа (84 базовых + 9 экзамен-вех) · icon-redesign: волны exam + Блок 1.
